import React, { useEffect, useState } from 'react';
import { Volume2, VolumeX, Play } from 'lucide-react';

export const useIsDesktop = (query = '(min-width: 1024px)') => {
  const [isDesktop, setIsDesktop] = useState(true);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const sync = () => setIsDesktop(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, [query]);
  return isDesktop;
};

/** Calls onLeave once the hero section is mostly scrolled out of view while sound is on. */
export const useAutoMuteOnScroll = (sectionRef, active, onLeave) => {
  useEffect(() => {
    if (!active || !sectionRef.current || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.intersectionRatio < 0.3) onLeave(); },
      { threshold: [0, 0.3, 0.6] }
    );
    io.observe(sectionRef.current);
    return () => io.disconnect();
  }, [sectionRef, active, onLeave]);
};

/** Mute/unmute pill + a subtle caption strip that fades in while sound is on. */
export const HeroMuteToggle = ({
  muted,
  onToggle,
  caption,
  testid,
  compact = false,
  align = 'right',
  onWatchFull,
  watchLabel = 'Watch the full video',
}) => {
  const Icon = muted ? VolumeX : Volume2;
  return (
    <div className={`flex flex-col gap-2 ${align === 'right' ? 'items-end' : 'items-start'}`}>
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={!muted}
        aria-label={muted ? 'Unmute video' : 'Mute video'}
        className="pointer-events-auto inline-flex items-center gap-2 rounded-full bg-blue-500/20 backdrop-blur-md border border-blue-300/30 hover:border-blue-300/70 hover:bg-blue-500/30 transition-colors px-3 py-1.5 text-blue-100"
        data-testid={testid}
      >
        <Icon className="w-3.5 h-3.5" aria-hidden="true" />
        <span className={`${compact ? 'text-[10px]' : 'text-[11px]'} font-semibold tracking-[0.18em] uppercase`}>
          {muted ? 'Unmute' : 'Mute'}
        </span>
      </button>
      {caption && (
        <p
          aria-live="polite"
          className={`pointer-events-none max-w-[280px] sm:max-w-sm rounded-md bg-slate-900/55 backdrop-blur-md border border-white/10 px-3 text-[11px] sm:text-xs text-blue-100/90 leading-snug overflow-hidden ${
            align === 'right' ? 'text-right' : 'text-left'
          } transition-all duration-500 ease-out ${
            muted ? 'opacity-0 translate-y-1 max-h-0 py-0 border-0' : 'opacity-100 translate-y-0 max-h-20 py-1.5'
          }`}
          data-testid={`${testid}-caption`}
        >
          {muted ? '' : caption}
        </p>
      )}
      {onWatchFull && (
        <button
          type="button"
          onClick={onWatchFull}
          className="pointer-events-auto inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-semibold text-blue-200 hover:text-white transition-colors underline-offset-4 hover:underline"
          data-testid={`${testid}-watch-full`}
        >
          <Play className="w-3 h-3" aria-hidden="true" />
          {watchLabel}
        </button>
      )}
    </div>
  );
};
